import React, { ReactElement, useState, useMemo, useCallback } from 'react';
import { Box, Heading, HStack, Switch, Text, useClipboard, VStack } from '@chakra-ui/react';
import { ModalWorksheetDistributionVariantProps, SharedModalProps } from 'types/Modal';
import { WorksheetStatusType } from 'types/StatusTypes';
import { toastContent } from 'config/toast';
import { SegmentControlWithContent } from 'components/common/SegmentControl/SegmentControlWithContent';
import {
  useGetClassesByWorksheet,
  useRemoveWorksheetFromClass,
  useConnectWorksheetToClass,
} from 'hooks/api/backend/managament/getClasses';
import { useUser } from 'utils/auth';
import { queryClient } from 'utils/react-query';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { downloadQRCode } from 'utils/worksheet/downloadQRCode';
import { SchoolIcon, UserIcon, UsersIcon } from 'assets/icons/v2';
import {
  useCreateWorksheetTemplate,
  useDeleteWorksheetTemplate,
} from 'hooks/api/backend/worksheets/useWorksheetTemplates';
import { useGetWorksheet } from 'hooks/api/backend/worksheets/useWorksheets';
import { ToastVariant } from 'types/Toast';
import { NovaIcon } from 'components/ui/novaIcon/NovaIcon';
import { useIsB2BTeacherOrAdmin } from 'hooks/utils/useIsB2BTeacherOrAdmin';
import { ClassesAccessDeniedMessage } from 'components/worksheet/dashboard/ClassesAccessDeniedMessage';
import { CustomModal } from '../../CustomModal';
import { CustomModalContent } from '../../CustomModalContent';
import { QRCodeDisplay } from './components/distributionModal/components/QRCodeDisplay';
import { RemoveConfirmationModal } from './components/distributionModal/ConfirmationModal';
import { QRShareModal } from './components/distributionModal/QRShareModal';
import { GroupsList } from './components/distributionModal/components/GroupsList';

type Props = SharedModalProps & ModalWorksheetDistributionVariantProps;

export const WorksheetDistributionModal = ({
  isShown,
  worksheetId,
  worksheetTitle,
  handleClose: closeModal,
}: Props): ReactElement => {
  const [loadingGroupId, setLoadingGroupId] = useState<string | null>(null);
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());
  const [groupToRemove, setGroupToRemove] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [qrShareGroup, setQrShareGroup] = useState<{ name: string } | null>(null);

  const navigationBarDesktop = document.querySelector('[data-id="navigation-bar-desktop"]');
  const navigationBarWidth = navigationBarDesktop?.clientWidth;
  const offsetLeft = navigationBarWidth ? `${navigationBarWidth / 2}px` : undefined;

  const { data: user } = useUser();
  const { isB2BUser, isB2BTeacherOrAdmin, isLoading: isAccessLoading } = useIsB2BTeacherOrAdmin();
  const { show: showToast } = useNovaToast();
  const canLoadClasses = Boolean(worksheetId) && isB2BTeacherOrAdmin === true;
  const { data: classesData, isLoading } = useGetClassesByWorksheet(
    worksheetId ?? '',
    canLoadClasses
  );

  const { mutateAsync: connectWorksheet } = useConnectWorksheetToClass();
  const { mutateAsync: removeWorksheet } = useRemoveWorksheetFromClass();
  const { mutateAsync: createWorksheetTemplate, isPending: isCreatingTemplate } =
    useCreateWorksheetTemplate();
  const { mutateAsync: deleteWorksheetTemplate, isPending: isDeletingTemplate } =
    useDeleteWorksheetTemplate();
  const { data: worksheetData, refetch: refetchWorksheet } = useGetWorksheet(worksheetId);
  const isSharedAsTemplate = Boolean(worksheetData?.isSharedAsTemplate);
  const sharedTemplateId = worksheetData?.sharedTemplateId;
  const cannotTurnOffTemplate = isSharedAsTemplate && worksheetData?.canDeleteTemplate === false;
  const isTemplateSwitchDisabled =
    isCreatingTemplate || isDeletingTemplate || cannotTurnOffTemplate;

  const handleAdd = useCallback(
    async (groupId: string): Promise<void> => {
      setLoadingGroupId(groupId);
      try {
        await connectWorksheet({
          classId: groupId,
          worksheetId: worksheetId ?? '',
        });
        queryClient.invalidateQueries({ queryKey: ['classes-by-worksheet', worksheetId] });
        setLoadingGroupId(null);
        showToast(toastContent.worksheet[WorksheetStatusType.CLASS_ADDED]);
      } catch (error) {
        setLoadingGroupId(null);
        showToast(toastContent.worksheet[WorksheetStatusType.CLASS_ADD_ERROR]);
      }
    },
    [connectWorksheet, worksheetId, showToast]
  );

  const handleRemoveClick = useCallback((groupId: string): void => {
    setGroupToRemove(groupId);
    setShowConfirmation(true);
  }, []);

  const handleCancelRemove = (): void => {
    setGroupToRemove(null);
    setShowConfirmation(false);
  };

  const confirmRemove = async (): Promise<void> => {
    if (!groupToRemove) return;

    setShowConfirmation(false);
    setLoadingGroupId(groupToRemove);
    try {
      await removeWorksheet({
        classId: groupToRemove,
        worksheetId: worksheetId ?? '',
      });
      queryClient.invalidateQueries({ queryKey: ['classes-by-worksheet', worksheetId] });
      setLoadingGroupId(null);
      setGroupToRemove(null);
      showToast(toastContent.worksheet[WorksheetStatusType.CLASS_REMOVED]);
    } catch (error) {
      setLoadingGroupId(null);
      setGroupToRemove(null);
      showToast(toastContent.worksheet[WorksheetStatusType.CLASS_REMOVE_ERROR]);
    }
  };

  const handleClose = (): void => {
    setLoadingGroupId(null);
    queryClient.invalidateQueries({ queryKey: ['classes-by-worksheet', worksheetId] });
    if (closeModal) closeModal();
  };

  const handleCreateTemplate = useCallback(async (): Promise<void> => {
    if (!worksheetId) return;
    try {
      await createWorksheetTemplate({
        sourceWorksheetId: worksheetId,
      });
      await refetchWorksheet();
      showToast({
        title: 'Vorlage erstellt',
        description: 'Dein Arbeitsblatt ist jetzt als Vorlage für deine Organisation verfügbar.',
        variant: ToastVariant.SUCCESS,
      });
    } catch (_error) {
      showToast({
        title: 'Vorlage konnte nicht erstellt werden',
        variant: ToastVariant.ERROR,
      });
    }
  }, [createWorksheetTemplate, refetchWorksheet, showToast, worksheetId]);

  const handleDeleteTemplate = useCallback(async (): Promise<void> => {
    if (!sharedTemplateId) return;
    try {
      await deleteWorksheetTemplate({
        templateId: sharedTemplateId,
      });

      await refetchWorksheet();

      showToast({
        title: 'Vorlage gelöscht',
        description:
          'Deine Vorlage ist jetzt nicht mehr als Vorlage für deine Organisation verfügbar.',
        variant: ToastVariant.SUCCESS,
      });
    } catch (_error) {
      showToast({
        title: 'Vorlage konnte nicht gelöscht werden',
        variant: ToastVariant.ERROR,
      });
    }
  }, [deleteWorksheetTemplate, refetchWorksheet, sharedTemplateId, showToast]);

  const handleTemplateSwitch = useCallback(
    async (nextChecked: boolean): Promise<void> => {
      if (nextChecked === isSharedAsTemplate) return;
      if (!nextChecked && worksheetData?.canDeleteTemplate === false) return;

      if (nextChecked) {
        await handleCreateTemplate();
        return;
      }

      await handleDeleteTemplate();
    },
    [
      handleCreateTemplate,
      handleDeleteTemplate,
      isSharedAsTemplate,
      worksheetData?.canDeleteTemplate,
    ]
  );

  const toggleGroupExpansion = useCallback((groupId: string): void => {
    setExpandedGroupIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  }, []);

  const { copy, setValue, copied } = useClipboard({
    value: `${window.location.origin}/app/worksheet/session/${worksheetId}`,
  });

  const handleCopy = useCallback((): void => {
    copy();
    setValue(`${window.location.origin}/app/worksheet/session/${worksheetId}`);
    // setTimeout(() => {
    //   handleClose();
    // }, 500);
  }, [copy, setValue, worksheetId]);

  const handleDownload = useCallback(
    (qrCodeRef: React.RefObject<SVGSVGElement>): void => {
      downloadQRCode(qrCodeRef, `novaheal-worksheet-${worksheetId}-qr-code`);
    },
    [worksheetId]
  );

  const worksheetUrl = `${window.location.origin}/app/worksheet/session/${worksheetId}`;

  const segments = useMemo(
    () => [
      {
        id: 'user',
        label: 'Link & QR-Code',
        icon: <UserIcon boxSize="16px" />,
        content: (
          <QRCodeDisplay
            worksheetUrl={worksheetUrl}
            hasCopied={copied}
            onCopy={handleCopy}
            onDownload={handleDownload}
          />
        ),
      },
      {
        id: 'groups',
        label: 'An Kurs austeilen',
        icon: <UsersIcon boxSize="16px" />,
        content: (
          <VStack gap="15px" align="stretch" width="100%" alignSelf="stretch">
            {(() => {
              if (isAccessLoading) {
                return (
                  <Text textStyle="body.s.regular" color="text.tertiary">
                    Lade Organisationsrechte...
                  </Text>
                );
              }

              if (!isB2BTeacherOrAdmin) {
                return <ClassesAccessDeniedMessage isB2BUser={isB2BUser} />;
              }

              return (
                <>
                  <Text textStyle="body.s.regular" color="text.quaternary">
                    Teile das Arbeitsblatt mit einem Kurs.
                  </Text>
                  <GroupsList
                    groups={classesData?.classes || []}
                    isLoading={isLoading}
                    expandedGroupIds={expandedGroupIds}
                    loadingGroupId={loadingGroupId}
                    onToggleExpansion={toggleGroupExpansion}
                    onQrShare={setQrShareGroup}
                    onRemove={handleRemoveClick}
                    onAdd={handleAdd}
                  />
                </>
              );
            })()}
          </VStack>
        ),
      },
      {
        id: 'organization',
        label: 'Organisation',
        icon: <SchoolIcon boxSize="16px" />,
        content: (
          <VStack gap="3xl" align="stretch" width="100%" alignSelf="stretch">
            {(() => {
              if (isAccessLoading) {
                return (
                  <Text textStyle="body.s.regular" color="text.tertiary">
                    Lade Organisationsrechte...
                  </Text>
                );
              }

              if (!isB2BTeacherOrAdmin) {
                return <ClassesAccessDeniedMessage isB2BUser={isB2BUser} />;
              }

              return (
                <>
                  <Text textStyle="body.s.regular" color="text.quaternary">
                    Speichere dieses Arbeitsblatt als Vorlage für deine Organisation oder entferne
                    die bestehende Vorlage.
                  </Text>
                  <VStack align="stretch" gap="m">
                    <Heading textStyle="h5.regular" color="text.primary">
                      Organisationsvorlage
                    </Heading>
                    <VStack
                      gap="10px"
                      border="default"
                      align="stretch"
                      borderColor="border.accent"
                      borderRadius="l"
                      px="s"
                      py="m"
                    >
                      <HStack justifyContent="space-between" alignItems="center" gap="12px">
                        <HStack gap="10px" flex="1" minW="0">
                          <NovaIcon icon={SchoolIcon} size="l" color="icon.tertiary" />
                          <VStack gap="0" align="flex-start" minW="0">
                            <Text textStyle="body.s.medium" color="text.primary" lineClamp={1}>
                              {user?.b2bSchool?.name || 'Organisation'}
                            </Text>
                          </VStack>
                        </HStack>
                        <Switch.Root
                          checked={isSharedAsTemplate}
                          disabled={isTemplateSwitchDisabled}
                          onCheckedChange={async (details) => {
                            await handleTemplateSwitch(details.checked);
                          }}
                        >
                          <Switch.HiddenInput />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      </HStack>
                    </VStack>
                  </VStack>
                </>
              );
            })()}
          </VStack>
        ),
      },
    ],
    [
      worksheetUrl,
      copied,
      handleCopy,
      handleDownload,
      classesData?.classes,
      isLoading,
      expandedGroupIds,
      loadingGroupId,
      toggleGroupExpansion,
      handleRemoveClick,
      handleAdd,
      isAccessLoading,
      isB2BUser,
      isB2BTeacherOrAdmin,
      user?.b2bSchool?.name,
      isSharedAsTemplate,
      handleTemplateSwitch,
      isTemplateSwitchDisabled,
    ]
  );

  return (
    <CustomModal
      isOpen={isShown}
      onClose={handleClose}
      title="Arbeitsblatt teilen"
      placement="center"
      shouldAlwaysStretchHeight
      size="lg"
    >
      <CustomModalContent
        p={{ base: '15px 20px 40px', md: '25px 20px' }}
        display="flex"
        flexDirection="column"
        flexGrow={1}
        minH={0}
      >
        <Box flex={1} minH={0} overflowY="auto" overscrollBehavior="contain">
          <SegmentControlWithContent
            segments={segments}
            defaultActiveId="user"
            keepMounted
            contentGap="l"
          />
        </Box>
        <RemoveConfirmationModal
          isOpen={showConfirmation}
          offsetLeft={offsetLeft}
          onConfirm={confirmRemove}
          onCancel={handleCancelRemove}
        />

        <QRShareModal
          isOpen={!!qrShareGroup}
          worksheetId={worksheetId || ''}
          worksheetTitle={worksheetTitle || ''}
          worksheetUrl={worksheetUrl}
          offsetLeft={offsetLeft}
          onClose={() => setQrShareGroup(null)}
        />
      </CustomModalContent>
    </CustomModal>
  );
};
